/* eslint-disable react/jsx-props-no-spreading */
import React, { FC, useCallback, useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { Icons, Positions, Sizes } from '@sede-enterprise/shell-ds-react-framework';
import { FormProvider, useForm } from 'react-hook-form';
import { Modal } from '../../NominationGroups/style';
import { Col, FormRow } from 'src/UI/Grid';
import Button from 'src/UI/Button';
import ErrorMessage from 'src/UI/ErrorMessage';
import FormControlTextInput from 'src/UI/FormComponent/FormControlTextInput';
import FormControlSelect from 'src/UI/FormComponent/FormControlSelect';
import FormControlDatePicker from 'src/UI/FormComponent/FormControlDatePicker';
import { get, post } from 'src/services/GenesisApi';
import { sendNotification } from 'src/UI/Notification';
import {
  GET_MARKETS,
  GET_ORGANIZATION_ROLES,
  MAX_LENGTH_20,
  MAX_LENGTH_50,
  MESSAGES,
  ParameterScope,
  SET_ORGANIZATION,
} from 'src/constants/EUMarkets';
import {
  IAddEditOrganization,
  ILocationParameterDto,
  IMarketItem,
  IRole,
  INGParameters,
  IParameterDefinition,
} from 'src/types/EUMarkets.type';
import { ISelectOptions } from 'src/types/Common.type';
import { handleError } from 'src/utils/common/utils';
import {
  formatDateOnly,
  hasOverlappingParameterDates,
  isDateRangeValid,
  withResolvedDefinitionIds,
} from '../../Locations/AddEditParameter';
import InlineParameterList, {
  getParameterKey,
} from '../../Locations/AddEditParameter/InlineParameterList';

const { Add: AddIcon } = Icons;

const defaultValues = {
  organizationName: '',
  shortName: '',
  marketId: null,
  validFrom: dayjs(),
  validTo: null,
  roleIds: [],
};

const toParameterDto = (parameter: INGParameters): ILocationParameterDto => ({
  parameterDefinitionId: parameter.parameterDefinitionId,
  valueString: parameter.valueString,
  valueLong: parameter.valueLong,
  valueDouble: parameter.valueDouble,
  valueBoolean: parameter.valueBoolean,
  valueListId: parameter.valueListId ?? null,
  valueListValue: parameter.valueListValue ?? null,
  validFrom: parameter.validFrom,
  validTo: parameter.validTo,
});

const areParameterDatesWithinOrganizationRange = (
  parameter: INGParameters,
  organizationValidFrom: Dayjs | undefined,
  organizationValidTo: Dayjs | null,
) => {
  if (!organizationValidFrom?.isValid()) {
    return true;
  }

  const parameterValidFrom = dayjs(parameter.validFrom);
  if (!parameterValidFrom.isValid() || parameterValidFrom.isBefore(organizationValidFrom, 'day')) {
    return false;
  }

  if (!organizationValidTo?.isValid()) {
    return true;
  }

  const parameterValidTo = parameter.validTo ? dayjs(parameter.validTo) : null;
  return Boolean(
    parameterValidTo?.isValid() && !parameterValidTo.isAfter(organizationValidTo, 'day'),
  );
};

const removeAndReindexParameterRecord = <T,>(records: Record<number, T>, parameterIndex: number) =>
  Object.entries(records).reduce<Record<number, T>>((next, [key, value]) => {
    const index = Number(key);
    if (index === parameterIndex) {
      return next;
    }

    next[index > parameterIndex ? index - 1 : index] = value;
    return next;
  }, {});

const AddEditOrganization: FC<IAddEditOrganization> = ({
  open = false,
  onClose = () => {},
  organization = null,
  refreshData = () => {},
}) => {
  const methods = useForm();
  const { reset, watch, setValue, setError, clearErrors } = methods;
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMarkets, setLoadingMarkets] = useState<boolean>(false);
  const [loadingRoles, setLoadingRoles] = useState<boolean>(false);
  const [markets, setMarkets] = useState<ISelectOptions[]>([]);
  const [roles, setRoles] = useState<ISelectOptions[]>([]);
  const [parameters, setParameters] = useState<INGParameters[]>([]);
  const [parameterDrafts, setParameterDrafts] = useState<Record<number, INGParameters>>({});
  const [parameterDraftErrors, setParameterDraftErrors] = useState<Record<number, string>>({});
  const [pendingParameterCount, setPendingParameterCount] = useState(0);
  const validFrom = watch?.('validFrom');
  const validTo = watch?.('validTo');
  const parametersErrorMessage = methods.formState?.errors?.parameters?.message as
    string | undefined;

  useEffect(() => {
    if (!open) {
      return;
    }
    setLoadingMarkets(true);
    get(GET_MARKETS)
      .then((response) => {
        setMarkets(
          (response.data || []).map((item: IMarketItem) => ({
            id: item.id,
            name: item.marketName,
          })),
        );
      })
      .catch((error) => handleError(error, MESSAGES.SERVER_ERROR))
      .finally(() => setLoadingMarkets(false));
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setLoadingRoles(true);
    get(GET_ORGANIZATION_ROLES)
      .then((response) => {
        setRoles(
          (response.data || []).map((role: IRole) => ({
            id: role.roleId,
            name: role.roleName,
          })),
        );
      })
      .catch((error) => handleError(error, MESSAGES.SERVER_ERROR))
      .finally(() => setLoadingRoles(false));
  }, [open]);

  useEffect(() => {
    if (organization) {
      reset({
        organizationName: organization.organizationName,
        shortName: organization.shortName,
        marketId: organization.marketId || null,
        validFrom: organization.validFrom ? dayjs(organization.validFrom) : undefined,
        validTo: organization.validTo ? dayjs(organization.validTo) : null,
        roleIds: organization.roles.map((role) => role.roleId),
      });
      setParameters(organization.parameters);
      setParameterDrafts({});
      setParameterDraftErrors({});
      setPendingParameterCount(0);
      return;
    }
    reset(defaultValues);
    setParameters([]);
    setParameterDrafts({});
    setParameterDraftErrors({});
    setPendingParameterCount(0);
  }, [organization, open, reset]);

  const handleClose = () => {
    reset(defaultValues);
    setParameters([]);
    setParameterDrafts({});
    setParameterDraftErrors({});
    setPendingParameterCount(0);
    clearErrors('parameters');
    onClose();
  };

  const handleValidFromChange = (newValidFrom: Dayjs | null) => {
    if (newValidFrom && validTo && dayjs(newValidFrom).isAfter(dayjs(validTo), 'day')) {
      setValue('validTo', null, { shouldValidate: true });
    }
  };

  const handleDefinitionsLoaded = useCallback((definitions: IParameterDefinition[]) => {
    setParameters((currentParameters) => withResolvedDefinitionIds(currentParameters, definitions));
  }, []);

  // Refreshes the banner only once one is showing, so newly added rows don't error before a save.
  const syncParametersError = useCallback(
    (nextErrors: Record<number, string>) => {
      const firstError = Object.values(nextErrors).find(Boolean);
      if (!firstError) {
        clearErrors('parameters');
        return;
      }
      if (parametersErrorMessage) {
        setError('parameters', { type: 'validate', message: firstError });
      }
    },
    [clearErrors, setError, parametersErrorMessage],
  );

  const handleParameterDraftChange = useCallback(
    (parameter: INGParameters, rowIndex: number, error?: string) => {
      setParameterDrafts((currentDrafts) => ({ ...currentDrafts, [rowIndex]: parameter }));
      setParameterDraftErrors((currentErrors) => {
        const nextErrors = { ...currentErrors, [rowIndex]: error ?? '' };
        syncParametersError(nextErrors);
        return nextErrors;
      });
    },
    [syncParametersError],
  );

  const handleParameterRemove = useCallback(
    (parameterIndex: number) => {
      setParameters((currentParameters) =>
        currentParameters.filter((_, index) => index !== parameterIndex),
      );
      setParameterDrafts((currentDrafts) =>
        removeAndReindexParameterRecord(currentDrafts, parameterIndex),
      );
      setParameterDraftErrors((currentErrors) => {
        const nextErrors = removeAndReindexParameterRecord(currentErrors, parameterIndex);
        if (!Object.values(nextErrors).some(Boolean)) {
          clearErrors('parameters');
        }
        return nextErrors;
      });
    },
    [clearErrors],
  );

  const handleCancelParameterAdd = useCallback(
    (parameterIndex?: number) => {
      setPendingParameterCount((currentCount) => Math.max(0, currentCount - 1));
      if (parameterIndex !== undefined) {
        setParameterDrafts((currentDrafts) =>
          removeAndReindexParameterRecord(currentDrafts, parameterIndex),
        );
        setParameterDraftErrors((currentErrors) => {
          const nextErrors = removeAndReindexParameterRecord(currentErrors, parameterIndex);
          if (!Object.values(nextErrors).some(Boolean)) {
            clearErrors('parameters');
          }
          return nextErrors;
        });
      }
    },
    [clearErrors],
  );

  const handleSubmit = (formData) => {
    const pendingParameterIndexes = Array.from(
      { length: pendingParameterCount },
      (_, index) => parameters.length + index,
    );
    const mergedParametersRaw = [
      ...parameters.map((parameter, index) => parameterDrafts[index] ?? parameter),
      ...pendingParameterIndexes
        .map((index) => parameterDrafts[index])
        .filter((parameter): parameter is INGParameters => Boolean(parameter)),
    ];
    const mergedParameters = mergedParametersRaw.filter(
      (parameter, index, allParameters) =>
        allParameters.findIndex(
          (candidate) => getParameterKey(candidate) === getParameterKey(parameter),
        ) === index,
    );
    const parameterValidationError = Object.values(parameterDraftErrors).find(Boolean);
    if (parameterValidationError) {
      setError('parameters', {
        type: 'validate',
        message: parameterValidationError,
      });
      return;
    }
    if (pendingParameterIndexes.some((index) => !parameterDrafts[index])) {
      setError('parameters', {
        type: 'validate',
        message: MESSAGES.REQUIRED_PARAMETER_DEFINITION,
      });
      return;
    }
    const organizationValidFrom = formData.validFrom as Dayjs | undefined;
    const organizationValidTo = (formData.validTo as Dayjs | null) ?? null;
    if (
      mergedParametersRaw.some(
        (parameter) =>
          !areParameterDatesWithinOrganizationRange(
            parameter,
            organizationValidFrom,
            organizationValidTo,
          ),
      )
    ) {
      setError('parameters', {
        type: 'validate',
        message: MESSAGES.PARAMETER_DATE_OUTSIDE_ORGANIZATION_RANGE,
      });
      return;
    }
    if (hasOverlappingParameterDates(mergedParametersRaw)) {
      setError('parameters', {
        type: 'validate',
        message: MESSAGES.OVERLAPPING_PARAMETER_DATE_RANGE,
      });
      return;
    }
    clearErrors('parameters');
    setLoading(true);
    const isUpdate = Boolean(organization);
    post(SET_ORGANIZATION, {
      id: organization?.id ?? 0,
      organizationName: formData.organizationName,
      shortName: formData.shortName,
      marketId: formData.marketId ? Number(formData.marketId) : undefined,
      isActive: organization?.isActive ?? true,
      validFrom: formatDateOnly(formData.validFrom as Dayjs),
      validTo: formData.validTo ? formatDateOnly(formData.validTo as Dayjs) : null,
      roleIds: formData.roleIds || [],
      parameters: mergedParameters.map(toParameterDto),
    })
      .then(() => {
        sendNotification({
          type: 'success',
          message: isUpdate ? MESSAGES.UPDATED_ORGANIZATION : MESSAGES.CREATED_ORGANIZATION,
        });
        refreshData();
        handleClose();
      })
      .catch((error) => {
        handleError(
          error,
          isUpdate ? MESSAGES.UPDATED_ORGANIZATION_FAILED : MESSAGES.CREATED_ORGANIZATION_FAILED,
        );
      })
      .finally(() => setLoading(false));
  };

  return (
    <Modal
      className='parameter-edit-modal'
      title={`${organization ? 'Modify' : 'Add a new'} organization`}
      open={open}
      onClose={handleClose}
      maskClosable={false}
      isDraggable
      width={1200}
      footer={
        <div className='button-style'>
          <Button type='button' onClick={handleClose} variant='outlined' data-testid='btn-cancel'>
            Cancel
          </Button>
          <Button
            data-testid='submit-form'
            type='button'
            loading={loading}
            disabled={loading}
            onClick={methods.handleSubmit(handleSubmit)}
          >
            Save
          </Button>
        </div>
      }
    >
      <FormProvider {...methods}>
        <FormRow className='modal-form-row'>
          <Col size={4}>
            <FormControlTextInput
              controllerProps={{
                name: 'organizationName',
                rules: {
                  required: MESSAGES.REQUIRED_ORGANIZATION_NAME,
                  maxLength: { value: MAX_LENGTH_50, message: MESSAGES.MAX_LENGTH_50 },
                },
              }}
              label='Organization Name'
              methods={methods}
              inputProps={{ placeholder: 'Enter Organization Name' }}
            />
          </Col>
          <Col size={4}>
            <FormControlTextInput
              controllerProps={{
                name: 'shortName',
                rules: {
                  required: MESSAGES.REQUIRED_SHORT_NAME,
                  maxLength: { value: MAX_LENGTH_20, message: MESSAGES.MAX_LENGTH_20 },
                },
              }}
              label='Short Name'
              methods={methods}
              inputProps={{ placeholder: 'Enter Short Name' }}
            />
          </Col>
          <Col size={4}>
            <FormControlSelect
              methods={methods}
              label='Market'
              isLoading={loadingMarkets}
              data={markets}
              controllerProps={{ name: 'marketId' }}
            />
          </Col>
          <Col size={4}>
            <FormControlSelect
              methods={methods}
              label='Roles'
              isLoading={loadingRoles}
              data={roles}
              controllerProps={{
                name: 'roleIds',
                rules: {
                  validate: (value) =>
                    (Array.isArray(value) ? value.length > 0 : Boolean(value)) ||
                    MESSAGES.REQUIRED_ROLE,
                },
              }}
              selectProps={{ allowClear: false, mode: 'multiple' }}
            />
          </Col>
          <Col size={4}>
            <FormControlDatePicker
              methods={methods}
              label='Valid From'
              controllerProps={{
                name: 'validFrom',
                rules: {
                  required: MESSAGES.REQUIRED_VALID_FROM,
                  validate: (value) =>
                    isDateRangeValid(value, validTo) || MESSAGES.INVALID_PARAMETER_DATE_RANGE,
                },
              }}
              handleOnChange={handleValidFromChange}
            />
          </Col>
          <Col size={4}>
            <FormControlDatePicker
              methods={methods}
              label='Valid To'
              controllerProps={{
                name: 'validTo',
                rules: {
                  validate: (value) =>
                    isDateRangeValid(validFrom, value) || MESSAGES.INVALID_PARAMETER_DATE_RANGE,
                },
              }}
              inputProps={{
                disabledDate: (current: Dayjs) =>
                  validFrom ? current.isBefore(validFrom, 'day') : false,
              }}
            />
          </Col>
        </FormRow>
        <div className='group-details group-details-with-action'>
          <strong className='param-title'>Organization Parameters</strong>
          <Button
            onClick={() => setPendingParameterCount((count) => count + 1)}
            icon={<AddIcon />}
            size={Sizes.Small}
            variant='outlined'
            iconPosition={Positions.Right}
            data-testid='add-organization-parameter'
          >
            Add Row
          </Button>
        </div>
        <div className='parameter-table'>
          <InlineParameterList
            open={open}
            scopeId={ParameterScope.Organization}
            locationId={organization?.id ?? 0}
            onDefinitionsLoaded={handleDefinitionsLoaded}
            parameters={parameters}
            isAdding={false}
            addingParameterCount={pendingParameterCount}
            onCancelAdd={handleCancelParameterAdd}
            onRemove={handleParameterRemove}
            onDraftChange={handleParameterDraftChange}
          />
          {parametersErrorMessage && <ErrorMessage>{parametersErrorMessage}</ErrorMessage>}
        </div>
      </FormProvider>
    </Modal>
  );
};

export default AddEditOrganization;
